import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/apiUtils.js';
import BudgetCalculator from './BudgetCalculator';
import { formatForInput, formatLocalDate, formatDateOnly, parseLocalDate, formatDateForExecutiveSummary, getWeekdayForExecutiveSummary } from '../utils/dateUtils';

const MeetingForm = ({ client, onSave, onCancel, initialMeetingData = null }) => {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [formData, setFormData] = useState({
    date: initialMeetingData?.date ? formatForInput(initialMeetingData.date) : formatForInput(new Date()),
    // Campos específicos de Google
    contactsGeneratedGoogle: initialMeetingData?.contactsGeneratedGoogle || 0,
    patientsGoogle: initialMeetingData?.patientsGoogle || 0,
    // Campos específicos de Facebook
    contactsGeneratedFacebook: initialMeetingData?.contactsGeneratedFacebook || 0,
    patientsFacebook: initialMeetingData?.patientsFacebook || 0,
    nextMeetingDate: initialMeetingData?.nextMeetingDate ? formatForInput(initialMeetingData.nextMeetingDate) : '',
    nextMeetingTime: initialMeetingData?.nextMeetingTime || '10:00',
    clientMeetingEmail: initialMeetingData?.clientMeetingEmail || client.email || '',
    meetingType: initialMeetingData?.meetingType || '',
    observations: initialMeetingData?.observations || '',
    agreements: initialMeetingData?.agreements || '',
    objections: initialMeetingData?.objections || {
      price: 0,
      location: 0,
      noResponse: 0,
      schedule: 0,
      other: ''
    },
    servicesSold: initialMeetingData?.servicesSold && initialMeetingData.servicesSold.length > 0 ? initialMeetingData.servicesSold : [
      { name: '', quantity: 0, value: 0 }
    ]
  });

  // Cargar usuarios desde la base de datos
  useEffect(() => {
    const loadUsers = async () => {
      try {
        console.log('Cargando usuarios desde la API...');
        const response = await apiFetch('/users');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const users = await response.json();
        console.log('Usuarios recibidos para tareas:', users);
        
        setAvailableUsers(users);
      } catch (error) {
        console.error('Error cargando usuarios:', error);
        console.error('Error details:', error.message);
      }
    };
    
    loadUsers();
  }, []);

  useEffect(() => {
    if (!initialMeetingData && client && client.meetings.length > 0) {
      const lastMeeting = client.meetings[client.meetings.length - 1];
      if (lastMeeting.nextMeetingDate) {
        setFormData(prev => ({
          ...prev,
          nextMeetingDate: lastMeeting.nextMeetingDate,
          nextMeetingTime: lastMeeting.nextMeetingTime || '10:00'
        }));
      }
    }
    if (!initialMeetingData && client.email) {
      setFormData(prev => ({
        ...prev,
        clientMeetingEmail: client.email
      }));
    }
  }, [client, initialMeetingData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'contactsGeneratedGoogle' || name === 'patientsGoogle' || 
              name === 'contactsGeneratedFacebook' || name === 'patientsFacebook'
        ? parseInt(value) || 0 
        : value
    }));
  };

  const handleObjectionChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      objections: {
        ...prev.objections,
        [name]: name === 'other' ? value : parseInt(value) || 0
      }
    }));
  };

  const handleServiceChange = (index, e) => {
    const { name, value } = e.target;
    const newServices = [...formData.servicesSold];
    newServices[index] = {
      ...newServices[index],
      [name]: name === 'name' ? value : parseFloat(value) || 0
    };
    setFormData(prev => ({ ...prev, servicesSold: newServices }));
  };

  const addServiceField = () => {
    setFormData(prev => ({
      ...prev,
      servicesSold: [...prev.servicesSold, { name: '', quantity: 0, value: 0 }]
    }));
  };

  const removeServiceField = (index) => {
    const newServices = formData.servicesSold.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, servicesSold: newServices }));
  };


  const calculateGoogleConversionRate = () => {
    if (formData.contactsGeneratedGoogle === 0) return '0%';
    const rate = (formData.patientsGoogle / formData.contactsGeneratedGoogle) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const calculateFacebookConversionRate = () => {
    if (formData.contactsGeneratedFacebook === 0) return '0%';
    const rate = (formData.patientsFacebook / formData.contactsGeneratedFacebook) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const calculateTotalConversionRate = () => {
    const totalContacts = (formData.contactsGeneratedGoogle || 0) + (formData.contactsGeneratedFacebook || 0);
    const totalPatients = (formData.patientsGoogle || 0) + (formData.patientsFacebook || 0);
    if (totalContacts === 0) return '0%';
    const rate = (totalPatients / totalContacts) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const generateClientHistorySection = async () => {
    let historySection = `\n\n${'='.repeat(80)}\n`;
    historySection += `HISTORIAL COMPLETO DEL CLIENTE - ${client.name.toUpperCase()}\n`;
    historySection += `${'='.repeat(80)}\n\n`;

    // Información básica del cliente
    historySection += `INFORMACIÓN DEL CLIENTE:\n`;
    historySection += `• Nombre: ${client.name}\n`;
    if (client.specialty) historySection += `• Especialidad: ${client.specialty}\n`;
    if (client.email) historySection += `• Email: ${client.email}\n`;
    if (client.phone) historySection += `• Teléfono: ${client.phone}\n`;
    if (client.country) historySection += `• País: ${client.country}\n`;
    if (client.cell) historySection += `• Célula: ${client.cell}\n`;
    historySection += `\n`;

    // Historial de juntas anteriores
    if (client.meetings && client.meetings.length > 0) {
      historySection += `HISTORIAL DE JUNTAS (${client.meetings.length} juntas registradas):\n`;
      historySection += `${'-'.repeat(60)}\n\n`;

      // Ordenar juntas por fecha cronológica
      const sortedMeetings = [...client.meetings].sort((a, b) => new Date(a.date) - new Date(b.date));

      sortedMeetings.forEach((meeting, index) => {
        const meetingNumber = index + 1;
        const meetingDate = formatLocalDate(meeting.date, { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });

        historySection += `JUNTA #${meetingNumber} - ${meetingDate}\n`;
        historySection += `${'-'.repeat(40)}\n`;
        
        if (meeting.meetingType) {
          historySection += `Tipo de Junta: ${meeting.meetingType}\n\n`;
        }
        
        // Métricas de la junta
        const totalContacts = meeting.contactsGenerated || 0;
        const totalPatients = meeting.patients || 0;
        const conversionRate = totalContacts > 0 ? ((totalPatients / totalContacts) * 100).toFixed(1) + '%' : '0%';
        
        historySection += `MÉTRICAS:\n`;
        historySection += `• Contactos Generados: ${totalContacts}\n`;
        historySection += `• Pacientes Concretados: ${totalPatients}\n`;
        historySection += `• Tasa de Conversión: ${conversionRate}\n\n`;

        // Resumen de la reunión
        if (meeting.observations) {
          historySection += `RESUMEN DE LA REUNIÓN:\n`;
          // Dividir el texto en párrafos para mejor legibilidad
          const observationParagraphs = meeting.observations.split('\n').filter(p => p.trim());
          observationParagraphs.forEach(paragraph => {
            historySection += `${paragraph.trim()}\n`;
          });
          historySection += `\n`;
        }

        // Acciones acordadas
        if (meeting.agreements) {
          historySection += `ACCIONES ACORDADAS:\n`;
          // Dividir el texto en párrafos para mejor legibilidad
          const agreementParagraphs = meeting.agreements.split('\n').filter(p => p.trim());
          agreementParagraphs.forEach(paragraph => {
            historySection += `${paragraph.trim()}\n`;
          });
          historySection += `\n`;
        }

        // Objeciones
        if (meeting.objections) {
          const totalObjections = (meeting.objections.price || 0) + (meeting.objections.location || 0) + 
                                 (meeting.objections.noResponse || 0) + (meeting.objections.schedule || 0);
          if (totalObjections > 0 || meeting.objections.other) {
            historySection += `OBJECIONES DETECTADAS:\n`;
            if (meeting.objections.price > 0) historySection += `• Precio: ${meeting.objections.price} personas\n`;
            if (meeting.objections.location > 0) historySection += `• Ubicación: ${meeting.objections.location} personas\n`;
            if (meeting.objections.noResponse > 0) historySection += `• No contestan primer mensaje: ${meeting.objections.noResponse} personas\n`;
            if (meeting.objections.schedule > 0) historySection += `• Horarios: ${meeting.objections.schedule} personas\n`;
            if (meeting.objections.other) historySection += `• Otras: ${meeting.objections.other}\n`;
            historySection += `\n`;
          }
        }

        // Servicios vendidos
        if (meeting.servicesSold && meeting.servicesSold.some(s => s.name)) {
          historySection += `SERVICIOS VENDIDOS:\n`;
          let totalValue = 0;
          meeting.servicesSold.forEach(service => {
            if (service.name) {
              const serviceValue = service.quantity * service.value;
              totalValue += serviceValue;
              historySection += `• ${service.name}: ${service.quantity} unidades, Valor: $${serviceValue.toFixed(2)}\n`;
            }
          });
          historySection += `• Total valor de servicios: $${totalValue.toFixed(2)}\n\n`;
        }

        // Próxima reunión programada
        if (meeting.nextMeetingDate) {
          const nextMeetingFormatted = formatLocalDate(meeting.nextMeetingDate, { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
          historySection += `PRÓXIMA REUNIÓN PROGRAMADA:\n`;
          historySection += `• Fecha: ${nextMeetingFormatted}`;
          if (meeting.nextMeetingTime) {
            historySection += ` a las ${meeting.nextMeetingTime} hrs`;
          }
          historySection += `\n\n`;
        }

        // Tareas de esta junta
        if (meeting.tasks && meeting.tasks.length > 0) {
          historySection += `TAREAS GENERADAS EN ESTA JUNTA (${meeting.tasks.length}):\n`;
          meeting.tasks.forEach((task, taskIndex) => {
            const taskNumber = taskIndex + 1;
            const taskStatus = task.completed ? '✅ COMPLETADA' : '⏳ PENDIENTE';
            historySection += `  ${taskNumber}. ${task.description || task.title || 'Sin descripción'}\n`;
            historySection += `     Estado: ${taskStatus}\n`;
            
            if (task.dueDate) {
              historySection += `     Fecha límite: ${formatDateOnly(task.dueDate)}\n`;
            }
            if (task.assignedTo) {
              historySection += `     Asignado a: ${task.assignedTo}\n`;
            }
            if (task.priority) {
              historySection += `     Prioridad: ${task.priority}\n`;
            }
            historySection += `\n`;
          });
        }

        historySection += `${'·'.repeat(60)}\n\n`;
      });
    } else {
      historySection += `No hay juntas anteriores registradas.\n\n`;
    }

    // Cargar todas las tareas del cliente desde el servidor
    try {
      let allClientTasks = [];
      
      // Primero, recopilar tareas de las juntas locales
      if (client.meetings && Array.isArray(client.meetings)) {
        client.meetings.forEach(meeting => {
          if (meeting.tasks && Array.isArray(meeting.tasks)) {
            meeting.tasks.forEach(task => {
              allClientTasks.push({
                ...task,
                meetingDate: meeting.date,
                meetingType: meeting.meetingType,
                meetingId: meeting.id,
                source: 'local'
              });
            });
          }
        });
      }

      // Luego, cargar tareas adicionales del servidor
      try {
        const meetingIds = client.meetings ? client.meetings.map(m => m.id).filter(id => id) : [];
        
        if (meetingIds.length > 0) {
          const tasksResponse = await apiFetch('/tasks');
          
          if (tasksResponse.ok) {
            const allServerTasks = await tasksResponse.json();
            
            // Filtrar solo las tareas que pertenecen a las juntas de este cliente
            const clientServerTasks = allServerTasks.filter(task => 
              meetingIds.includes(task.meetingId)
            );
            
            // Agregar tareas del servidor que no estén ya en los datos locales
            clientServerTasks.forEach(serverTask => {
              const existsInLocal = allClientTasks.some(localTask => 
                localTask.id === serverTask.id || 
                (localTask.meetingId === serverTask.meetingId && localTask.description === serverTask.description)
              );
              
              if (!existsInLocal) {
                // Encontrar la junta correspondiente
                const correspondingMeeting = client.meetings.find(m => m.id === serverTask.meetingId);
                
                allClientTasks.push({
                  ...serverTask,
                  meetingDate: correspondingMeeting ? correspondingMeeting.date : (serverTask.createdAt || new Date().toISOString()),
                  meetingType: correspondingMeeting ? correspondingMeeting.meetingType : 'Tarea del sistema',
                  meetingId: serverTask.meetingId,
                  source: 'server'
                });
              }
            });
          }
        }
      } catch (serverError) {
        console.log('Error al cargar tareas del servidor para historial:', serverError);
      }

      // Resumen de todas las tareas
      if (allClientTasks.length > 0) {
        // Ordenar tareas por fecha
        allClientTasks.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(a.meetingDate);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(b.meetingDate);
          return dateA - dateB;
        });

        const completedTasks = allClientTasks.filter(t => t.completed);
        const pendingTasks = allClientTasks.filter(t => !t.completed);

        historySection += `RESUMEN COMPLETO DE TAREAS:\n`;
        historySection += `${'-'.repeat(60)}\n`;
        historySection += `Total de tareas: ${allClientTasks.length}\n`;
        historySection += `Tareas completadas: ${completedTasks.length}\n`;
        historySection += `Tareas pendientes: ${pendingTasks.length}\n`;
        historySection += `Porcentaje de completitud: ${allClientTasks.length > 0 ? ((completedTasks.length / allClientTasks.length) * 100).toFixed(1) : 0}%\n\n`;

        // Detalle de todas las tareas
        historySection += `DETALLE COMPLETO DE TODAS LAS TAREAS:\n`;
        historySection += `${'-'.repeat(60)}\n\n`;

        allClientTasks.forEach((task, index) => {
          const taskNumber = index + 1;
          const taskStatus = task.completed ? '✅ COMPLETADA' : '⏳ PENDIENTE';
          const taskDate = task.createdAt ? formatDateOnly(task.createdAt) : formatDateOnly(task.meetingDate);
          
          historySection += `TAREA #${taskNumber} - ${taskStatus}\n`;
          historySection += `Fecha: ${taskDate}\n`;
          historySection += `Junta: ${task.meetingType || 'Sin especificar'}\n`;
          historySection += `Descripción: ${task.description || task.title || 'Sin descripción'}\n`;
          
          if (task.dueDate) {
            historySection += `Fecha límite: ${formatDateOnly(task.dueDate)}\n`;
          }
          if (task.assignedTo) {
            historySection += `Asignado a: ${task.assignedTo}\n`;
          }
          if (task.priority) {
            historySection += `Prioridad: ${task.priority}\n`;
          }
          if (task.cell) {
            historySection += `Célula: ${task.cell}\n`;
          }
          if (task.platform) {
            historySection += `Plataforma: ${task.platform}\n`;
          }
          if (task.area) {
            historySection += `Área: ${task.area}\n`;
          }
          
          if (task.completed && task.completedAt) {
            historySection += `Completada el: ${formatDateOnly(task.completedAt)}\n`;
          }
          if (task.completed && task.completedBy) {
            historySection += `Completada por: ${task.completedBy}\n`;
          }
          if (task.completionComment) {
            historySection += `Comentario de completitud: ${task.completionComment}\n`;
          }
          
          historySection += `\n`;
        });
      } else {
        historySection += `No hay tareas registradas para este cliente.\n\n`;
      }

    } catch (error) {
      console.error('Error al generar historial de tareas:', error);
      historySection += `Error al cargar el historial completo de tareas.\n\n`;
    }

    historySection += `${'='.repeat(80)}\n`;
    historySection += `FIN DEL HISTORIAL COMPLETO\n`;
    historySection += `${'='.repeat(80)}\n`;

    return historySection;
  };

  const generateExecutiveSummary = async () => {
    const formattedDate = formatLocalDate(formData.date, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    // SOLUCIÓN SIMPLE: Usar directamente la fecha de la BD sin conversiones complicadas
    let nextMeetingDateTime = '';
    if (formData.nextMeetingDate && formData.nextMeetingTime) {
      // Crear fecha directamente desde los componentes de la fecha de la BD
      const [year, month, day] = formData.nextMeetingDate.split('-').map(Number);
      const meetingDate = new Date(year, month - 1, day); // Usar fecha local directa
      
      const weekday = meetingDate.toLocaleDateString('es-MX', { weekday: 'long' });
      const formattedDate = meetingDate.toLocaleDateString('es-MX', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      nextMeetingDateTime = `${weekday}, ${formattedDate} a las ${formData.nextMeetingTime} hrs`;
    }

    let objectionsSummary = '';
    const totalObjections = formData.objections.price + formData.objections.location + formData.objections.noResponse + formData.objections.schedule;
    if (totalObjections > 0 || formData.objections.other) {
      objectionsSummary += `\n\nOBJECIONES DETECTADAS:\n`;
      if (formData.objections.price > 0) objectionsSummary += `• Precio: ${formData.objections.price} personas\n`;
      if (formData.objections.location > 0) objectionsSummary += `• Ubicación: ${formData.objections.location} personas\n`;
      if (formData.objections.noResponse > 0) objectionsSummary += `• No contestan primer mensaje: ${formData.objections.noResponse} personas\n`;
      if (formData.objections.schedule > 0) objectionsSummary += `• Horarios: ${formData.objections.schedule} personas\n`;
      if (formData.objections.other) objectionsSummary += `• Otras: ${formData.objections.other}\n`;
    }

    let servicesSummary = '';
    const totalServicesValue = formData.servicesSold.reduce((sum, service) => sum + (service.quantity * service.value), 0);
    if (formData.servicesSold.some(s => s.name)) {
      servicesSummary += `\n\nSERVICIOS VENDIDOS:\n`;
      formData.servicesSold.forEach(service => {
        if (service.name) {
          servicesSummary += `• ${service.name}: ${service.quantity} unidades, Valor: $${(service.quantity * service.value).toFixed(2)}\n`;
        }
      });
      servicesSummary += `Total valor de servicios vendidos: $${totalServicesValue.toFixed(2)}\n`;
    }

    // Generar el historial completo del cliente
    const clientHistory = await generateClientHistorySection();

    // Calcular totales y tasas de conversión
    const totalContactsGoogle = formData.contactsGeneratedGoogle || 0;
    const totalPatientsGoogle = formData.patientsGoogle || 0;
    const totalContactsFacebook = formData.contactsGeneratedFacebook || 0;
    const totalPatientsFacebook = formData.patientsFacebook || 0;
    const totalContacts = totalContactsGoogle + totalContactsFacebook;
    const totalPatients = totalPatientsGoogle + totalPatientsFacebook;

    const googleConversionRate = totalContactsGoogle > 0 ? ((totalPatientsGoogle / totalContactsGoogle) * 100).toFixed(1) + '%' : '0%';
    const facebookConversionRate = totalContactsFacebook > 0 ? ((totalPatientsFacebook / totalContactsFacebook) * 100).toFixed(1) + '%' : '0%';
    const totalConversionRate = totalContacts > 0 ? ((totalPatients / totalContacts) * 100).toFixed(1) + '%' : '0%';

    return `MINUTA EJECUTIVA - ${client.name.toUpperCase()}\n` +
           `Fecha: ${formattedDate}\n\n` +
           `___________________________________________\n\n` +
           `CLIENTE: ${client.name}\n` +
           (client.specialty ? `ESPECIALIDAD: ${client.specialty}\n\n` : '\n') +
           (formData.meetingType ? `TIPO DE JUNTA: ${formData.meetingType}\n\n` : '\n') +
           `MÉTRICAS DE RENDIMIENTO POR PLATAFORMA:\n\n` +
           `GOOGLE ADS:\n` +
           `• Contactos generados: ${totalContactsGoogle}\n` +
           `• Pacientes concretados: ${totalPatientsGoogle}\n` +
           `• Tasa de conversión: ${googleConversionRate}\n\n` +
           `FACEBOOK ADS:\n` +
           `• Contactos generados: ${totalContactsFacebook}\n` +
           `• Pacientes concretados: ${totalPatientsFacebook}\n` +
           `• Tasa de conversión: ${facebookConversionRate}\n\n` +
           `TOTALES GENERALES:\n` +
           `• Total contactos generados: ${totalContacts}\n` +
           `• Total pacientes concretados: ${totalPatients}\n` +
           `• Tasa de conversión general: ${totalConversionRate}\n\n` +
           `RESUMEN DE LA REUNIÓN:\n${formData.observations}\n\n` +
           `ACCIONES ACORDADAS:\n${formData.agreements || 'No se registraron acciones específicas'}\n` +
           objectionsSummary +
           servicesSummary +
           (nextMeetingDateTime ? `\nPRÓXIMA REUNIÓN: ${nextMeetingDateTime}\n\n` : '\n') +
           `___________________________________________\n` +
           `Generado el ${formatDateOnly(new Date())}` +
           clientHistory;
  };

  const generateGoogleCalendarLink = () => {
    if (!formData.nextMeetingDate || !formData.nextMeetingTime) return '';

    const [hours, minutes] = formData.nextMeetingTime.split(':').map(Number);
    const [year, month, day] = formData.nextMeetingDate.split('-').map(Number);
    
    // SOLUCIÓN: Formatear fecha local directamente sin conversión UTC
    // Esto evita que el cambio de zona horaria altere el día
    const formatLocalDateTime = (year, month, day, hour, minute) => {
      const yearStr = year.toString();
      const monthStr = month.toString().padStart(2, '0');
      const dayStr = day.toString().padStart(2, '0');
      const hourStr = hour.toString().padStart(2, '0');
      const minuteStr = minute.toString().padStart(2, '0');
      return `${yearStr}${monthStr}${dayStr}T${hourStr}${minuteStr}00`;
    };

    const startDateTime = formatLocalDateTime(year, month, day, hours, minutes);
    const endDateTime = formatLocalDateTime(year, month, day, hours + 1, minutes);

    const title = encodeURIComponent(`Doctorweb (${client.name}) - ${formData.meetingType || 'Junta'}`);
    const description = encodeURIComponent(`Esta junta es para revisar los avances de los ajustes realizados en la última junta.\n\nResumen de la junta anterior:\n${formData.observations}\n\nAcciones acordadas:\n${formData.agreements}`);
    const dates = `${startDateTime}/${endDateTime}`;
    const clientEmail = formData.clientMeetingEmail ? `&add=${encodeURIComponent(formData.clientMeetingEmail)}` : '';

    const reminders = `&reminders=true&reminderminutes=1440&reminderminutes=180&reminderminutes=60`;

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${description}${clientEmail}${reminders}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mostrar indicador de carga
    const submitButton = e.target.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Generando minuta con historial completo...';
    submitButton.disabled = true;

    try {
      const executiveSummary = await generateExecutiveSummary();
      const googleCalendarLink = generateGoogleCalendarLink();

      const meetingToSave = {
        id: initialMeetingData?.id || Date.now(),
        ...formData,
        executiveSummary,
        clientName: client.name,
        googleCalendarLink,
        // Asegurar que los campos de Google y Facebook se incluyan explícitamente
        contactsGeneratedGoogle: formData.contactsGeneratedGoogle || 0,
        patientsGoogle: formData.patientsGoogle || 0,
        contactsGeneratedFacebook: formData.contactsGeneratedFacebook || 0,
        patientsFacebook: formData.patientsFacebook || 0,
        // Calcular totales para compatibilidad
        contactsGenerated: (formData.contactsGeneratedGoogle || 0) + (formData.contactsGeneratedFacebook || 0),
        patients: (formData.patientsGoogle || 0) + (formData.patientsFacebook || 0)
      };
      
      onSave(meetingToSave);

      if (googleCalendarLink) {
        alert("Minuta generada con historial completo. Ahora se abrirá Google Calendar para agendar la próxima reunión.");
        window.open(googleCalendarLink, '_blank');
      } else {
        alert("Minuta generada con historial completo. No se agendó en Google Calendar porque no se especificó una próxima fecha y/u hora de junta.");
      }
    } catch (error) {
      console.error('Error al generar la minuta:', error);
      alert('Error al generar la minuta. Por favor, inténtelo de nuevo.');
    } finally {
      // Restaurar el botón
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  };

  const meetingTypes = [
    '',
    'Seguimiento de Campañas de Marketing',
    'Capacitación de Role Play',
    'Capacitación de Estrategia de Precio y Oferta',
    'Seguimiento de Role Play'
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-6">

      {/* Formulario de Nueva Junta */}
      <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-[#0061A8] flex items-center space-x-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>{initialMeetingData ? `Editar Junta con ${client.name}` : `Nueva Junta con ${client.name}`}</span>
          </h3>
          {client.specialty && <p className="text-sm text-[#63666A] mt-1">Especialidad: {client.specialty}</p>}
          {client.meetings && client.meetings.length > 0 && (
            <p className="text-sm text-blue-600 mt-1">
              📊 Consulta el historial arriba para contexto de juntas anteriores
            </p>
          )}
        </div>

        {/* Información básica */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Fecha de junta</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#63666A]">Tipo de Junta</label>
            <select
              name="meetingType"
              value={formData.meetingType}
              onChange={handleChange}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              {meetingTypes.map(type => (
                <option key={type} value={type}>{type === '' ? 'Seleccione un tipo de junta' : type}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[#63666A]">Email del Cliente para Agendamiento</label>
            <input
              type="email"
              name="clientMeetingEmail"
              value={formData.clientMeetingEmail}
              onChange={handleChange}
              placeholder="ejemplo@cliente.com"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
        </div>

        {/* Métricas por Plataforma */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Google Ads */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-blue-500 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <h4 className="text-lg font-bold text-blue-700">Google Ads</h4>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-2">Contactos generados en Google</label>
                <input
                  type="number"
                  name="contactsGeneratedGoogle"
                  value={formData.contactsGeneratedGoogle}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-blue-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-700 mb-2">Pacientes concretados en Google</label>
                <input
                  type="number"
                  name="patientsGoogle"
                  value={formData.patientsGoogle}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-blue-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-700 mb-2">Tasa de conversión Google</label>
                <div className="w-full px-4 py-3 bg-blue-200 rounded-lg text-blue-800 font-semibold text-center">
                  {calculateGoogleConversionRate()}
                </div>
              </div>
            </div>
          </div>

          {/* Facebook Ads */}
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 border-2 border-indigo-200">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-indigo-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <h4 className="text-lg font-bold text-indigo-700">Facebook Ads</h4>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-indigo-700 mb-2">Contactos generados en Facebook</label>
                <input
                  type="number"
                  name="contactsGeneratedFacebook"
                  value={formData.contactsGeneratedFacebook}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-indigo-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-indigo-700 mb-2">Pacientes concretados en Facebook</label>
                <input
                  type="number"
                  name="patientsFacebook"
                  value={formData.patientsFacebook}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-indigo-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-indigo-700 mb-2">Tasa de conversión Facebook</label>
                <div className="w-full px-4 py-3 bg-indigo-200 rounded-lg text-indigo-800 font-semibold text-center">
                  {calculateFacebookConversionRate()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resumen Total */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200 mb-6">
          <h4 className="text-lg font-bold text-green-700 mb-4 flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen Total de Ambas Plataformas
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-green-600">{(formData.contactsGeneratedGoogle || 0) + (formData.contactsGeneratedFacebook || 0)}</div>
              <div className="text-sm text-green-700">Total Contactos</div>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-green-600">{(formData.patientsGoogle || 0) + (formData.patientsFacebook || 0)}</div>
              <div className="text-sm text-green-700">Total Pacientes</div>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-green-600">{calculateTotalConversionRate()}</div>
              <div className="text-sm text-green-700">Tasa Total</div>
            </div>
          </div>
        </div>

        {/* Próxima Junta */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Próxima junta de seguimiento</label>
            <input
              type="date"
              name="nextMeetingDate"
              value={formData.nextMeetingDate}
              onChange={handleChange}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Hora de la próxima junta</label>
            <input
              type="time"
              name="nextMeetingTime"
              value={formData.nextMeetingTime}
              onChange={handleChange}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
        </div>

      <div>
        <label className="block text-sm font-medium text-[#63666A]">Resumen de la reunión</label>
        <textarea
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          rows={4}
          placeholder="Describa los puntos clave tratados en la reunión..."
          className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-[#63666A]">Acciones acordadas</label>
        <textarea
          name="agreements"
          value={formData.agreements}
          onChange={handleChange}
          rows={3}
          placeholder="Enumere las acciones acordadas durante la reunión..."
          className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
        />
      </div>


      {/* Sección de Servicios Vendidos */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-bold text-green-700 flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            Servicios Vendidos
          </h4>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-600">
              ${formData.servicesSold.reduce((sum, service) => sum + (service.quantity * service.value), 0).toFixed(2)}
            </div>
            <div className="text-sm text-green-700">Total Vendido</div>
          </div>
        </div>

        {formData.servicesSold.map((service, index) => (
          <div key={index} className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-green-200">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-green-700 mb-2">Nombre del Servicio</label>
                <input
                  type="text"
                  name="name"
                  value={service.name}
                  onChange={(e) => handleServiceChange(index, e)}
                  className="w-full border border-green-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Ej. Consulta inicial"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-green-700 mb-2">Cantidad</label>
                <input
                  type="number"
                  name="quantity"
                  value={service.quantity}
                  onChange={(e) => handleServiceChange(index, e)}
                  min="0"
                  className="w-full border border-green-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-green-700 mb-2">Valor Unitario</label>
                <input
                  type="number"
                  name="value"
                  value={service.value}
                  onChange={(e) => handleServiceChange(index, e)}
                  min="0"
                  step="0.01"
                  className="w-full border border-green-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="0.00"
                />
              </div>
              <div className="flex flex-col items-center space-y-2">
                <div className="text-sm font-medium text-green-700">Subtotal</div>
                <div className="bg-green-100 px-3 py-2 rounded-lg text-green-800 font-bold min-w-[80px] text-center">
                  ${(service.quantity * service.value).toFixed(2)}
                </div>
                {formData.servicesSold.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeServiceField(index)}
                    className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-colors"
                    title="Eliminar servicio"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        <div className="flex justify-between items-center mt-6">
          <button
            type="button"
            onClick={addServiceField}
            className="bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Agregar Servicio</span>
          </button>
          
          <div className="bg-white rounded-lg p-4 shadow-md border-2 border-green-300">
            <div className="text-center">
              <div className="text-sm text-green-700 mb-1">TOTAL GENERAL</div>
              <div className="text-3xl font-bold text-green-600">
                ${formData.servicesSold.reduce((sum, service) => sum + (service.quantity * service.value), 0).toFixed(2)}
              </div>
              <div className="text-xs text-green-600 mt-1">
                {formData.servicesSold.filter(s => s.name).length} servicio{formData.servicesSold.filter(s => s.name).length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sección de Objeciones */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-[#D1D3D4]">
        <h4 className="text-lg font-semibold text-[#0061A8] mb-4">Objeciones (Pacientes que no agendan)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Por Precio</label>
            <input
              type="number"
              name="price"
              value={formData.objections.price}
              onChange={handleObjectionChange}
              min="0"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Por Ubicación</label>
            <input
              type="number"
              name="location"
              value={formData.objections.location}
              onChange={handleObjectionChange}
              min="0"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">No contestan 1er mensaje</label>
            <input
              type="number"
              name="noResponse"
              value={formData.objections.noResponse}
              onChange={handleObjectionChange}
              min="0"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Por Horarios</label>
            <input
              type="number"
              name="schedule"
              value={formData.objections.schedule}
              onChange={handleObjectionChange}
              min="0"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[#63666A]">Otras Objeciones (describir)</label>
            <textarea
              name="other"
              value={formData.objections.other}
              onChange={handleObjectionChange}
              rows="2"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            ></textarea>
          </div>
        </div>
      </div>

      {/* Calculadora de Presupuesto integrada */}
      <BudgetCalculator clientCountry={client.country || 'México'} />

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="border border-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
        >
          {initialMeetingData ? 'Guardar Cambios' : 'Generar Minuta y Agendar'}
        </button>
      </div>
      </form>

      {/* HISTORIAL COMPLETO DEL CLIENTE AL FINAL */}
      {client.meetings && client.meetings.length > 0 && (
        <div className="mt-8 p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl shadow-lg border border-gray-200">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-[#0061A8] rounded-xl">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-[#0061A8]">Historial Completo del Cliente - {client.name}</h3>
            <span className="bg-[#0061A8] text-white px-3 py-1 rounded-full text-sm font-medium">
              {client.meetings.length} junta{client.meetings.length !== 1 ? 's' : ''} registrada{client.meetings.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Información del Cliente */}
          <div className="bg-white rounded-xl p-6 mb-6 shadow-md">
            <h4 className="text-lg font-semibold text-[#0061A8] mb-4">📋 Información del Cliente</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div><strong>Nombre:</strong> {client.name}</div>
              {client.specialty && <div><strong>Especialidad:</strong> {client.specialty}</div>}
              {client.email && <div><strong>Email:</strong> {client.email}</div>}
              {client.phone && <div><strong>Teléfono:</strong> {client.phone}</div>}
              {client.country && <div><strong>País:</strong> {client.country}</div>}
              {client.cell && <div><strong>Célula:</strong> {client.cell}</div>}
            </div>
          </div>

          {/* Historial de Juntas */}
          <div className="bg-white rounded-xl p-6 mb-6 shadow-md">
            <h4 className="text-lg font-semibold text-[#0061A8] mb-4">📅 Historial de Juntas ({client.meetings.length} juntas registradas)</h4>
            <div className="space-y-6">
              {client.meetings
                .slice()
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((meeting, chronologicalIndex) => {
                  const meetingNumber = chronologicalIndex + 1;
                  const totalContacts = meeting.contactsGenerated || 0;
                  const totalPatients = meeting.patients || 0;
                  const conversionRate = totalContacts > 0 ? ((totalPatients / totalContacts) * 100).toFixed(1) + '%' : '0%';
                  
                  return (
                    <div key={meeting.id || chronologicalIndex} className="border-l-4 border-[#0061A8] pl-6 pb-6">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="bg-[#0061A8] text-white px-3 py-1 rounded-full text-sm font-bold">
                          Junta #{meetingNumber}
                        </span>
                        <span className="text-lg font-semibold text-gray-700">
                          {formatLocalDate(meeting.date, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                        {meeting.meetingType && (
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                            {meeting.meetingType}
                          </span>
                        )}
                      </div>

                      {/* Métricas */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-[#0061A8]">{totalContacts}</div>
                          <div className="text-sm text-gray-600">Contactos Generados</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">{totalPatients}</div>
                          <div className="text-sm text-gray-600">Pacientes Concretados</div>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">{conversionRate}</div>
                          <div className="text-sm text-gray-600">Tasa de Conversión</div>
                        </div>
                      </div>

                      {/* Resumen de la Reunión */}
                      {meeting.observations && (
                        <div className="mb-4">
                          <h5 className="font-semibold text-gray-700 mb-2">📝 Resumen de la Reunión:</h5>
                          <div className="bg-gray-50 p-3 rounded-lg text-gray-700">
                            <div className="whitespace-pre-wrap">
                              {meeting.observations}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Acciones Acordadas */}
                      {meeting.agreements && (
                        <div className="mb-4">
                          <h5 className="font-semibold text-gray-700 mb-2">✅ Acciones Acordadas:</h5>
                          <div className="bg-green-50 p-3 rounded-lg text-gray-700">
                            <div className="whitespace-pre-wrap">
                              {meeting.agreements}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Objeciones */}
                      {meeting.objections && (
                        (() => {
                          const totalObjections = (meeting.objections.price || 0) + (meeting.objections.location || 0) + 
                                                 (meeting.objections.noResponse || 0) + (meeting.objections.schedule || 0);
                          if (totalObjections > 0 || meeting.objections.other) {
                            return (
                              <div className="mb-4">
                                <h5 className="font-semibold text-gray-700 mb-2">⚠️ Objeciones Detectadas:</h5>
                                <div className="bg-yellow-50 p-3 rounded-lg">
                                  {meeting.objections.price > 0 && <div>• Precio: {meeting.objections.price} personas</div>}
                                  {meeting.objections.location > 0 && <div>• Ubicación: {meeting.objections.location} personas</div>}
                                  {meeting.objections.noResponse > 0 && <div>• No contestan primer mensaje: {meeting.objections.noResponse} personas</div>}
                                  {meeting.objections.schedule > 0 && <div>• Horarios: {meeting.objections.schedule} personas</div>}
                                  {meeting.objections.other && <div>• Otras: {meeting.objections.other}</div>}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()
                      )}

                      {/* Servicios Vendidos */}
                      {meeting.servicesSold && meeting.servicesSold.some(s => s.name) && (
                        <div className="mb-4">
                          <h5 className="font-semibold text-gray-700 mb-2">💰 Servicios Vendidos:</h5>
                          <div className="bg-green-50 p-3 rounded-lg">
                            {meeting.servicesSold.map((service, serviceIndex) => {
                              if (service.name) {
                                const serviceValue = service.quantity * service.value;
                                return (
                                  <div key={serviceIndex}>
                                    • {service.name}: {service.quantity} unidades, Valor: ${serviceValue.toFixed(2)}
                                  </div>
                                );
                              }
                              return null;
                            })}
                            <div className="font-semibold mt-2">
                              Total valor: ${meeting.servicesSold.reduce((sum, service) => sum + (service.quantity * service.value), 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Próxima Reunión */}
                      {meeting.nextMeetingDate && (
                        <div className="mb-4">
                          <h5 className="font-semibold text-gray-700 mb-2">📅 Próxima Reunión Programada:</h5>
                          <div className="bg-orange-50 p-3 rounded-lg text-orange-700">
                            {formatLocalDate(meeting.nextMeetingDate, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            {meeting.nextMeetingTime && ` a las ${meeting.nextMeetingTime} hrs`}
                          </div>
                        </div>
                      )}

                      {/* Tareas de esta Junta */}
                      {meeting.tasks && meeting.tasks.length > 0 && (
                        <div className="mb-4">
                          <h5 className="font-semibold text-gray-700 mb-2">📋 Tareas Generadas en esta Junta ({meeting.tasks.length}):</h5>
                          <div className="bg-blue-50 p-3 rounded-lg space-y-2">
                            {meeting.tasks.map((task, taskIndex) => {
                              const taskStatus = task.completed ? '✅ COMPLETADA' : '⏳ PENDIENTE';
                              return (
                                <div key={taskIndex} className="flex items-start space-x-2">
                                  <span className="font-medium">{taskIndex + 1}.</span>
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <span>{task.description || task.title || 'Sin descripción'}</span>
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        task.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {taskStatus}
                                      </span>
                                    </div>
                                    {task.dueDate && <div className="text-sm text-gray-600">Fecha límite: {formatDateOnly(task.dueDate)}</div>}
                                    {task.assignedTo && <div className="text-sm text-gray-600">Asignado a: {task.assignedTo}</div>}
                                    {task.priority && <div className="text-sm text-gray-600">Prioridad: {task.priority}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Resumen de Todas las Tareas */}
          {(() => {
            const allTasks = client.meetings.reduce((tasks, meeting) => {
              if (meeting.tasks && Array.isArray(meeting.tasks)) {
                return tasks.concat(meeting.tasks.map(task => ({
                  ...task,
                  meetingDate: meeting.date,
                  meetingType: meeting.meetingType,
                  meetingId: meeting.id
                })));
              }
              return tasks;
            }, []);

            if (allTasks.length > 0) {
              const pendingTasks = allTasks.filter(t => !t.completed);
              const completedTasks = allTasks.filter(t => t.completed && !t.approved);
              const approvedTasks = allTasks.filter(t => t.approved);

              return (
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h4 className="text-lg font-semibold text-[#0061A8] mb-4">📊 Resumen Completo de Tareas</h4>
                  
                  {/* Estadísticas */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-[#0061A8]">{allTasks.length}</div>
                      <div className="text-sm text-gray-600">Total de Tareas</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{pendingTasks.length}</div>
                      <div className="text-sm text-gray-600">Pendientes</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{completedTasks.length}</div>
                      <div className="text-sm text-gray-600">Completadas</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{approvedTasks.length}</div>
                      <div className="text-sm text-gray-600">Aprobadas</div>
                    </div>
                  </div>

                  {/* Secciones de Tareas por Estado */}
                  <div className="space-y-6">
                    
                    {/* Tareas Pendientes */}
                    {pendingTasks.length > 0 && (
                      <div>
                        <h5 className="font-semibold text-yellow-700 mb-3 flex items-center">
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm mr-2">
                            {pendingTasks.length}
                          </span>
                          ⏳ Tareas Pendientes
                        </h5>
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {pendingTasks
                            .sort((a, b) => new Date(a.meetingDate) - new Date(b.meetingDate))
                            .map((task, index) => (
                              <div key={`pending-${index}`} className="p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-gray-700">{task.description || task.title || 'Sin descripción'}</span>
                                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                                    PENDIENTE
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  <span>📅 {formatDateOnly(task.meetingDate)}</span>
                                  {task.assignedTo && <span className="ml-4">👤 {task.assignedTo}</span>}
                                  {task.priority && <span className="ml-4">🔥 {task.priority}</span>}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Tareas Completadas (No Aprobadas) */}
                    {completedTasks.length > 0 && (
                      <div>
                        <h5 className="font-semibold text-green-700 mb-3 flex items-center">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm mr-2">
                            {completedTasks.length}
                          </span>
                          ✅ Tareas Completadas (Pendientes de Aprobación)
                        </h5>
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {completedTasks
                            .sort((a, b) => new Date(a.meetingDate) - new Date(b.meetingDate))
                            .map((task, index) => (
                              <div key={`completed-${index}`} className="p-3 bg-green-50 border-l-4 border-green-500 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-gray-700">{task.description || task.title || 'Sin descripción'}</span>
                                  <div className="flex items-center space-x-2">
                                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                      COMPLETADA
                                    </span>
                                    <button
                                      onClick={() => {
                                        // Aquí iría la lógica para aprobar la tarea
                                        console.log('Aprobar tarea:', task.id);
                                        alert('Funcionalidad de aprobación pendiente de implementar en el servidor');
                                      }}
                                      className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-purple-600 transition-colors"
                                    >
                                      APROBAR
                                    </button>
                                  </div>
                                </div>
                                <div className="text-sm text-gray-600">
                                  <span>📅 {formatDateOnly(task.meetingDate)}</span>
                                  {task.completedBy && <span className="ml-4">✅ Por: {task.completedBy}</span>}
                                  {task.completedAt && <span className="ml-4">📅 {formatDateOnly(task.completedAt)}</span>}
                                </div>
                                {task.completionComment && (
                                  <div className="text-sm text-gray-600 mt-2 bg-white p-2 rounded">
                                    💬 {task.completionComment}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Tareas Aprobadas */}
                    {approvedTasks.length > 0 && (
                      <div>
                        <h5 className="font-semibold text-purple-700 mb-3 flex items-center">
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm mr-2">
                            {approvedTasks.length}
                          </span>
                          🎉 Tareas Aprobadas
                        </h5>
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {approvedTasks
                            .sort((a, b) => new Date(a.meetingDate) - new Date(b.meetingDate))
                            .map((task, index) => (
                              <div key={`approved-${index}`} className="p-3 bg-purple-50 border-l-4 border-purple-500 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-gray-700">{task.description || task.title || 'Sin descripción'}</span>
                                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                                    APROBADA
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  <span>📅 {formatDateOnly(task.meetingDate)}</span>
                                  {task.completedBy && <span className="ml-4">✅ Por: {task.completedBy}</span>}
                                  {task.approvedBy && <span className="ml-4">👑 Aprobada por: {task.approvedBy}</span>}
                                  {task.approvedAt && <span className="ml-4">📅 {formatDateOnly(task.approvedAt)}</span>}
                                </div>
                                {task.completionComment && (
                                  <div className="text-sm text-gray-600 mt-2 bg-white p-2 rounded">
                                    💬 {task.completionComment}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
};

export default MeetingForm;
